import React, { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import './autocomplete.css'

const CheckIcon = () => (
	<svg
		className="h-4 w-4"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M5 13l4 4L19 7"
		/>
	</svg>
)

const ClockIcon = () => (
	<svg
		className="h-4 w-4"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
		/>
	</svg>
)

const SpinnerIcon = () => (
	<svg
		className="h-4 w-4 animate-spin"
		fill="none"
		viewBox="0 0 24 24"
	>
		<circle
			className="opacity-25"
			cx="12"
			cy="12"
			r="10"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			className="opacity-75"
			fill="currentColor"
			d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
		/>
	</svg>
)

export default function AutocompleteCityDestination({
	style,
	className,
	name,
	isDarkMode,
	locations = [],
	onToggleSelection,
}) {
	const handleToggleWithLimit = useCallback(
		cityData => {
			const selectedCount = locations.filter(loc => loc.isSelected).length
			const cityName =
				typeof cityData === 'string'
					? cityData
					: `${cityData.city}, ${cityData.state_id}`
			const isAlreadySelected = locations.find(
				loc => loc.name === cityName
			)?.isSelected

			// Allow if deselecting or if under limit
			if (isAlreadySelected || selectedCount < 3) {
				onToggleSelection(cityData)
			} else {
				toast.warning('Maximum 3 destination locations allowed')

				setInputValue('')
				setSearchTerm('')
				setCities([])
			}
		},
		[locations, onToggleSelection]
	)
	const [inputValue, setInputValue] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [cities, setCities] = useState([])
	const [isFocused, setIsFocused] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)

	// Show joined city names when blurred
	useEffect(() => {
		if (!isFocused) {
			const selected = locations
				.filter(loc => loc.isSelected)
				.map(loc => loc.name)
			setInputValue(selected.join(', '))
		}
	}, [locations, isFocused])

	// Fetch cities
	const fetchCities = useCallback(async value => {
		if (!value || value.length < 3) {
			setCities([])
			setHasSearched(false)
			return
		}
		if (value.length > 100) {
			toast.warning('Search query must not exceed 100 characters')
			setCities([])
			return
		}
		setIsLoading(true)
		try {
			const response = await axios.get('/api/geocode', {
				params: { query: value },
			})
			setCities(response.data.features)
			setHasSearched(true)
		} catch (err) {
			console.error('Failed to fetch cities:', err)
			setCities([])
		} finally {
			setIsLoading(false)
		}
	}, [])

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (searchTerm) fetchCities(searchTerm)
		}, 500)
		return () => clearTimeout(timeout)
	}, [searchTerm, fetchCities])

	const handleFocus = () => {
		setIsFocused(true)
		setInputValue('')
		setSearchTerm('')
		setHasSearched(false)
	}

	const handleBlur = () => {
		setIsFocused(false)
	}

	const handleChange = e => {
		const value = e.target.value
		setInputValue(value)
		setSearchTerm(value)
	}

	const handleSelect = useCallback(
		city => {
			// Clear server results immediately to prevent UI lag
			setCities([])
			setInputValue('')
			setSearchTerm('')

			const selectedCount = locations.filter(loc => loc.isSelected).length
			const cityName = `${city.city}, ${city.state_id}`
			const isAlreadySelected = locations.find(
				loc => loc.name === cityName
			)?.isSelected

			// Allow if deselecting or if under limit
			if (isAlreadySelected || selectedCount < 3) {
				onToggleSelection(city)
			} else {
				toast.warning('Maximum 3 destination locations allowed')
			}
		},
		[locations, onToggleSelection]
	)

	return (
		<div className="autocomplete-container">
			<input
				id="destination-input"
				value={inputValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				className={className}
				name={name}
				style={style}
				title=""
				data-tooltip-id=""
				data-tooltip-disable="true"
				autoComplete="off"
			/>

			{isFocused && (cities.length > 0 || locations.length > 0 || isLoading || hasSearched) && (
				<ul
					className={`dropdown-menu ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
				>
					{isLoading ? (
						<li
							className={`dropdown-item ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
							style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}
						>
							<SpinnerIcon />
							<span>Searching...</span>
						</li>
					) : cities.length > 0 ? (
						cities.map((city, i) => {
							const name = `${city.city}, ${city.state_id}`
							const selected = locations.find(
								loc => loc.name === name
							)?.isSelected
							return (
								<li
									key={`search-${i}`}
									className={`dropdown-item ${
										isDarkMode ? 'dark-mode' : 'light-mode'
									}`}
									onMouseDown={e => e.preventDefault()}
									onClick={() => handleSelect(city)}
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										cursor: 'pointer',
									}}
								>
									<span>{name}</span>
									{selected && <CheckIcon />}
								</li>
							)
						})
					) : hasSearched && searchTerm.length >= 3 ? (
						<li
							className={`dropdown-item ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
							style={{ cursor: 'default', color: isDarkMode ? '#9ca3af' : '#6b7280' }}
						>
							No matching cities found
						</li>
					) : (
						locations.map((location, i) => (
							<li
								key={`stored-${i}`}
								className={`dropdown-item ${
									isDarkMode ? 'dark-mode' : 'light-mode'
								} ${location.isSelected ? 'selected-item' : ''}`}
								onMouseDown={e => e.preventDefault()}
								onClick={() =>
									handleToggleWithLimit({
										city: location.name.split(', ')[0],
										state_id: location.name.split(', ')[1],
									})
								}
								style={{
									display: 'flex',
									alignItems: 'center',
									cursor: 'pointer',
									fontWeight: location.isSelected ? 'bold' : 'normal',
									gap: '8px',
								}}
							>
								{location.isSelected ? <CheckIcon /> : <ClockIcon />}
								<span>{location.name}</span>
							</li>
						))
					)}
				</ul>
			)}
		</div>
	)
}
